import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Polling cadence, chosen by what the user is actually looking at. A conversation on screen needs
// to feel live; a badge in the app bar does not.
export const POLL_THREAD_MS = 8000;
export const POLL_INBOX_MS = 20000;
export const POLL_BACKGROUND_MS = 45000;

/**
 * One shared interval for message delivery.
 *
 * A hidden tab polls not at all — an interval that keeps firing on a backgrounded tab is the
 * usual reason an idle app still generates steady request load. Coming back into view triggers an
 * immediate catch-up poll rather than waiting out the next tick, so returning to the tab shows
 * current state straight away.
 *
 * @param {object}   options
 * @param {string}   [options.threadId]  the open conversation, if any
 * @param {string}   [options.since]     ISO timestamp of the newest post already held
 * @param {function} [options.onPosts]   (posts) => void, called with posts newer than `since`
 * @param {function} [options.onUnread]  (count) => void, called with the unread thread count
 * @param {boolean}  [options.active]    false to suspend polling entirely
 * @param {number}   [options.intervalMs] overrides the cadence; the app bar badge uses
 *                                        POLL_BACKGROUND_MS because nobody is watching it
 */
export default function useMessagePoll({ threadId, since, onPosts, onUnread, active = true, intervalMs }) {
    const [visible, setVisible] = useState(() =>
        typeof document === 'undefined' ? true : document.visibilityState !== 'hidden');

    // The callbacks and cursor live in refs so a re-render with a new closure does not restart the
    // interval — otherwise every incoming post would reset the timer and the cadence would drift.
    const onPostsRef = useRef(onPosts);
    const onUnreadRef = useRef(onUnread);
    const sinceRef = useRef(since);
    const threadRef = useRef(threadId);
    const inflight = useRef(false);

    useEffect(() => { onPostsRef.current = onPosts; }, [onPosts]);
    useEffect(() => { onUnreadRef.current = onUnread; }, [onUnread]);
    useEffect(() => { sinceRef.current = since; }, [since]);
    useEffect(() => { threadRef.current = threadId; }, [threadId]);

    useEffect(() => {
        const onVisibility = () => setVisible(document.visibilityState !== 'hidden');
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    const poll = useCallback(async () => {
        // Skip rather than queue: a slow response should not be followed by a burst of the polls
        // that stacked up behind it.
        if (inflight.current) return;
        inflight.current = true;
        try {
            const params = {};
            if (threadRef.current != null) params.threadId = threadRef.current;
            if (sinceRef.current != null) params.since = sinceRef.current;

            const res = await axios.get('/api/message/poll', { params });
            const posts = res.data.data || [];
            if (posts.length > 0 && onPostsRef.current) onPostsRef.current(posts);
            if (onUnreadRef.current) onUnreadRef.current(res.data.unreadThreads || 0);
        } catch {
            // A dropped poll is not worth interrupting the user for; the next tick retries.
        } finally {
            inflight.current = false;
        }
    }, []);

    useEffect(() => {
        if (!active) return undefined;
        if (!visible) return undefined; // Hidden tab: paused, and caught up on return.

        // Catch up immediately, then settle into the cadence the current view deserves.
        poll();
        const interval = intervalMs != null
            ? intervalMs
            : (threadId != null ? POLL_THREAD_MS : POLL_INBOX_MS);
        const timer = setInterval(poll, interval);
        return () => clearInterval(timer);
    }, [active, visible, threadId, intervalMs, poll]);

    return { poll };
}
